import React, { useContext, useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { DATE, DROPDOWN, mapToAnswerProp, QUESTION, TEXT } from 'common/constants/process-management';
import { useStore } from 'common/store';
import { Answer, Option } from 'common/types/firm';
import { UseStoreValues } from 'common/types/store';
import { TaskContext } from 'context/TaskContext';
import { TaskContextType } from 'context/TaskContext/types';
import { Question } from 'dashboard409a/pages/Information/types';
import { useTasks } from 'services/hooks';
import ProcessManagementQuestionsFormDateInput from './ProcessManagementQuestionsFormDateInput/ProcessManagementQuestionsFormDateInput';
import ProcessManagementQuestionsFormDropdownInput from './ProcessManagementQuestionsFormDropdownInput/ProcessManagementQuestionsFormDropdownInput';
import { ProcessManagementQuestionsFormInputProps, QuestionAnswerData } from './ProcessManagementQuestionsFormInput.props';
import ProcessManagementQuestionsFormTextInput from './ProcessManagementQuestionsFormTextInput/ProcessManagementQuestionsFormTextInput';
import { extractAnswer } from '../../extractAnswer';

const ProcessManagementQuestionsFormInput = ({
  question,
  getQuestionStatus,
  setEditableQuestionId,
}: ProcessManagementQuestionsFormInputProps) => {
  const { question_type: questionType, answer, answers, options, children_questions: childrenQuestions } = question;
  const { updateQuestionnaireRequestToken } = useTasks();
  const { pairedHmacTokenAndId, validateToken } = useContext(TaskContext) as TaskContextType;
  const { pairedId, pairedHmacToken } = pairedHmacTokenAndId;
  const [{ user }] = useStore() as unknown as UseStoreValues;
  const [answerToDisplay, setAnswerToDisplay] = useState<string | null>(answer as string | null);
  const [childrenAnswer, setChildrenAnswer] = useState<string>('');

  const isAnswerModified = (answerParam: string | number, questionParam: Question) => {
    const extractedAnswer = extractAnswer(questionParam);
    if (extractedAnswer) {
      const propToAccess = mapToAnswerProp[questionParam.question_type];
      return (extractedAnswer)[propToAccess as keyof Answer] !== answerParam;
    }
    // there isn't an answer stored
    return true;
  };

  const mapAnswerToAnswerProps = (answerParam: string | number, questionParam: Question) => {
    const extractedAnswer = extractAnswer(questionParam);
    const storedId = extractedAnswer ? extractedAnswer.id : null;
    return {
      [QUESTION]: questionParam.id,
      [mapToAnswerProp[questionParam.question_type]]: answerParam,
      ...(storedId && { id: storedId }),
    };
  };

  const handleAnswerQuestion = async (answerParam: string | number) => {
    const tmpIsQuestionModified = isAnswerModified(answerParam, question);
    if (tmpIsQuestionModified) {
      const mappedAnswer = mapAnswerToAnswerProps(answerParam, question);
      updateQuestionnaireRequestToken(String(pairedId), String(pairedHmacToken), mappedAnswer as QuestionAnswerData);
      setAnswerToDisplay(answerParam.toString());
      validateToken();
      if (isEmpty(childrenQuestions)) {
        setEditableQuestionId(null);
      } else {
        // Pin the form open so child questions remain visible until answered
        setEditableQuestionId(question.id);
      }
    }
  };

  useEffect(() => {
    const updateAnswers = async () => {
      if (!isEmpty(childrenQuestions) && !isEmpty(childrenAnswer)) {
        const questionParam = childrenQuestions[0]; // only one child question is supported for now
        if (isAnswerModified(childrenAnswer, questionParam)) {
          const mappedAnswer = mapAnswerToAnswerProps(childrenAnswer, questionParam);
          await updateQuestionnaireRequestToken(String(pairedId), String(pairedHmacToken), mappedAnswer as QuestionAnswerData);
          validateToken();
          setEditableQuestionId(null);
        }
      }
    };
    updateAnswers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [childrenAnswer, pairedHmacToken, pairedId]);

  useEffect(() => {
    switch (questionType) {
      case DATE:
        setAnswerToDisplay(user && answers?.length ? answers[0]?.date_answer ?? '' : answer ?? '');
        break;
      case TEXT:
        setAnswerToDisplay(user && answers?.length ? answers[0]?.text_answer ?? '' : answer ?? '');
        break;
      case DROPDOWN:
        setAnswerToDisplay(user && answers?.length ? answers[0]?.selected_choice?.toString() ?? '' : answer ?? '');
        break;
      default:
        break;
    }
  }, [answer, answers, questionType, user]);

  switch (questionType) {
    case DATE:
      return (
        <ProcessManagementQuestionsFormDateInput
          answerToDisplay={answerToDisplay ?? ''}
          /* React 19:
          By convention, any callback called inside `startTransition`
          should be named `action` or include the `Action` suffix.
          */
          handleAnswerQuestionAction={handleAnswerQuestion}
        />
      );
    case TEXT:
      return (
        <ProcessManagementQuestionsFormTextInput
          answerToDisplay={answerToDisplay ?? ''}
          handleAnswerQuestion={handleAnswerQuestion}
          placeholder="Write your answer here"
          isAnswered={getQuestionStatus(question)}
        />
      );
    case DROPDOWN:
      return (
        <ProcessManagementQuestionsFormDropdownInput
          answerToDisplay={parseFloat(answerToDisplay ?? '0') || 0}
          handleAnswerQuestion={handleAnswerQuestion}
          options={options as unknown as Option[]}
          childrenQuestions={childrenQuestions}
          setChildrenAnswer={setChildrenAnswer}
          getQuestionStatus={getQuestionStatus}
        />
      );
    default:
      return <div />;
  }
};

export default ProcessManagementQuestionsFormInput;
